'use client';

import { useState } from 'react';
import type { ReachOutRequest } from '@cabal/shared/domain/system/types.js';
import { useReachOut } from '@/hooks/integrations';
import { ContactFormStep } from './footerModels';
import type { FooterContactFields } from './useFooterContactFields';

interface UseFooterContactSubmissionInput {
  fields: FooterContactFields;
  setContactStep: (contactStep: ContactFormStep) => void;
  setCommittedMessage: (message: string) => void;
  onContactClick?: () => void;
}

export interface FooterContactSubmissionController {
  isSubmitting: boolean;
  handleSubmit: () => void;
}

export function useFooterContactSubmission({
  fields,
  setContactStep,
  setCommittedMessage,
  onContactClick,
}: UseFooterContactSubmissionInput): FooterContactSubmissionController {
  const reachOut = useReachOut();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (): void => {
    if (!fields.validateMessage()) {
      return;
    }

    setIsSubmitting(true);
    setCommittedMessage(fields.message);

    const requestData: ReachOutRequest = {
      name: fields.name,
      email: fields.email,
      message: fields.message,
    };

    reachOut.invoke(requestData, {
      onSuccess: (response) => {
        fields.resetContactFormFields();
        if (response.success) {
          setContactStep(ContactFormStep.DoneStep);
          onContactClick?.();
        } else {
          setContactStep(ContactFormStep.ErrorStep);
        }
      },
      onError: () => {
        fields.resetContactFormFields();
        setContactStep(ContactFormStep.ErrorStep);
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  return {
    isSubmitting,
    handleSubmit,
  };
}
